/** Gift lines a visitor collects on /gift. Adding a gift needs no account:
 *  a logged-out visitor's lines wait in this browser and move to the server
 *  cart right after sign-in (gifts reserve no questions, so nothing is lost
 *  by waiting). */
export type GiftLine = (
	| { kind: 'premade'; uuid: string }
	| { kind: 'quick'; questions: number }
) & {
	/** Failed attempts to move this saved gift to the server cart. */
	tries?: number;
};

export const QUICK_GIFT_OPTIONS = [20, 30, 40, 50];

/** Same as the quick products (price_cents); logged-out visitors can't read
 *  them, every /api route but the catalogue needs a login. */
export const QUICK_GIFT_PRICES: Record<number, number> = {
	20: 4,
	30: 6,
	40: 8,
	50: 10,
};

// a gift's questions are picked on redemption, so the buyer chooses no types
const GIFT_QUESTION_TYPES = [
	'standard',
	'abcd',
	'connection',
	'association',
	'visual',
];

const STORAGE_KEY = 'giftBasket';

export const giftLineKey = (line: GiftLine) =>
	line.kind === 'premade' ? `p:${line.uuid}` : `q:${line.questions}`;

export const giftLineBody = (line: GiftLine) =>
	line.kind === 'premade'
		? { premade_quiz_uuid: line.uuid, gift: true }
		: {
				quick_quiz: {
					number_of_questions: line.questions,
					question_types: GIFT_QUESTION_TYPES,
				},
				gift: true,
			};

const isGiftLine = (line: unknown): line is GiftLine => {
	const l = line as GiftLine | null;
	if (l?.kind === 'premade') return typeof l.uuid === 'string';
	if (l?.kind === 'quick') return QUICK_GIFT_OPTIONS.includes(l.questions);
	return false;
};

export const readGuestGifts = (): GiftLine[] => {
	try {
		const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
		return Array.isArray(parsed) ? parsed.filter(isGiftLine) : [];
	} catch {
		return [];
	}
};

export const writeGuestGifts = (lines: GiftLine[]) => {
	try {
		if (lines.length) localStorage.setItem(STORAGE_KEY, JSON.stringify(lines));
		else localStorage.removeItem(STORAGE_KEY);
	} catch {
		// storage unavailable (private mode etc.): the basket lasts this page view
	}
};

export const GIFT_BASKET_STORAGE_KEY = STORAGE_KEY;

export const hasGuestGifts = () => readGuestGifts().length > 0;
